import { PortableText } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import Figure from 'components/sanity/Figure';

import { CheckListItem, UnorderedList } from './CheckList';
import { H1, H2, H3, H4 } from './Typography';

type Props = {
  content: PortableTextBlock | PortableTextBlock[];
  headingStyles?: {
    h1?: { as?: 'h2'; className?: string; size?: 'md' | 'xl' | '2xl'; headingStyle?: boolean };
    h2?: { className?: string; headingStyle?: boolean };
    h3?: { className?: string; headingStyle?: boolean };
    h4?: { className?: string; headingStyle?: boolean };
  };
  linkIcon?: JSX.Element;
  linkParams?: string;
  noCheckmarkList?: boolean;
};

const CustomPortableText = ({ content, headingStyles, linkIcon, linkParams, noCheckmarkList }: Props) => {
  return (
    <PortableText
      onMissingComponent={false}
      value={content}
      components={{
        types: {
          blockContentImg: ({ value }) => <Figure node={value} />,
        },
        marks: {
          link: ({ value, children }: { value?: { href?: string }; children: ReactNode }) => {
            const isInternal = /^\/(?!\/)/.test(value?.href ?? '');
            return (
              <a
                className={twMerge(clsx('hover:underline', !!linkIcon && 'flex flex-row items-center gap-4'))}
                href={`${value?.href}${linkParams ? linkParams : ''}`}
                {...(!isInternal && { target: '_blank', rel: 'noopener noreferrer nofollow' })}
              >
                {children}
                {linkIcon && linkIcon}
              </a>
            );
          },
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          strike: ({ children }) => <s>{children}</s>,
          sup: ({ children }) => <sup>{children}</sup>,
          nbsp: ({ children }) => <span className="whitespace-nowrap">{children}</span>,
        },
        block: {
          h1: ({ children }) => <H1 {...headingStyles?.h1}>{children}</H1>,
          h2: ({ children }) => <H2 {...headingStyles?.h2}>{children}</H2>,
          h3: ({ children }) => <H3 {...headingStyles?.h3}>{children}</H3>,
          h4: ({ children }) => <H4 {...headingStyles?.h4}>{children}</H4>,
          normal: ({ children }) => <p className="text-md font-normal leading-normal md:text-lg">{children}</p>,
          small: ({ children }) => <p className="small !text-xs !font-normal leading-normal md:!text-sm">{children}</p>,
        },
        list: {
          bullet: ({ children }) => <UnorderedList>{children}</UnorderedList>,
          number: ({ children }) => <ol className="list-inside list-decimal pl-16">{children}</ol>,
        },
        listItem: {
          bullet: ({ children }) => (
            <CheckListItem
              className="[&_h3_strong]:mb-16 [&_h3_strong]:block [&_ul_p]:font-normal [&_ul_p]:leading-normal [&_ul_span]:font-normal [&_ul_span]:leading-normal"
              noCheckmarkList={noCheckmarkList}
            >
              {children}
            </CheckListItem>
          ),
          number: ({ children }) => <li className="leading-175 text-sm md:text-lg">{children}</li>,
        },
      }}
    />
  );
};

export default CustomPortableText;
